/**
 * ============================================================================
 * HEADER DESKTOP BIKE TO GO - JAVASCRIPT PROFISSIONAL
 * ============================================================================
 * Versão: 1.0.0
 * Projeto: BikeTogo System — Bike To Go Floripa
 *
 * FUNCIONALIDADES:
 * - Sticky header com animação suave
 * - Troca de logo automática
 * - Detecção de página (HOME vs OUTRAS)
 * - Dropdown/submenu com hover
 * - Hamburger menu (mobile)
 * - Performance otimizada com requestAnimationFrame
 * - Acessibilidade (keyboard navigation)
 * ============================================================================
 */

(function() {
  'use strict';

  /**
   * HeaderBTG Class
   * Gerencia todas as funcionalidades do header
   */
  class HeaderBTG {
    constructor() {
      // Elementos principais
      this.header = document.querySelector('.header-btg');
      this.gradient = document.querySelector('.header-btg__gradient');
      this.hamburger = document.querySelector('.header-btg__hamburger');
      this.menuItems = document.querySelectorAll('.header-btg__menu-item--has-dropdown');

      // Configurações do data attributes
      this.stickyType = this.header?.dataset.stickyType || 'always';
      this.stickyThreshold = parseInt(this.header?.dataset.stickyThreshold) || 100;
      this.headerMode = this.header?.dataset.headerMode || 'page';

      // Estado
      this.isSticky = false;
      this.lastScrollY = 0;
      this.ticking = false;
      this.headerHeight = 0;

      // Inicializar
      if (this.header) {
        this.init();
      }
    }

    /**
     * Inicialização
     */
    init() {
      // Calcular altura do header
      this.calculateHeaderHeight();

      // Configurar sticky
      if (this.stickyType !== 'none') {
        this.setupSticky();
      }

      // Configurar dropdowns
      this.setupDropdowns();

      // Configurar hamburger
      if (this.hamburger) {
        this.setupHamburger();
      }

      // Configurar acessibilidade
      this.setupAccessibility();

      // Listener de resize
      window.addEventListener('resize', this.debounce(() => {
        this.calculateHeaderHeight();
      }, 250));

      // Log de inicialização
      console.log('[HeaderBTG] Inicializado', {
        mode: this.headerMode,
        stickyType: this.stickyType,
        threshold: this.stickyThreshold
      });
    }

    /**
     * Calcular altura do header
     */
    calculateHeaderHeight() {
      if (this.header) {
        this.headerHeight = this.header.offsetHeight;
      }
    }

    /**
     * Configurar Sticky Header
     */
    setupSticky() {
      // Listener de scroll otimizado
      window.addEventListener('scroll', () => {
        if (!this.ticking) {
          requestAnimationFrame(() => {
            this.handleScroll();
            this.ticking = false;
          });
          this.ticking = true;
        }
      }, { passive: true });

      // Verificar estado inicial
      this.handleScroll();
    }

    /**
     * Handler de Scroll
     */
    handleScroll() {
      const currentScrollY = window.scrollY;

      // Determinar se deve ativar sticky
      let shouldBeSticky = false;

      switch (this.stickyType) {
        case 'always':
          // Sticky sempre após threshold
          shouldBeSticky = currentScrollY > this.stickyThreshold;
          break;

        case 'up':
          // Sticky apenas ao rolar para cima
          if (currentScrollY > this.stickyThreshold) {
            shouldBeSticky = currentScrollY < this.lastScrollY;
          }
          break;

        case 'down':
          // Sticky apenas ao rolar para baixo
          if (currentScrollY > this.stickyThreshold) {
            shouldBeSticky = currentScrollY > this.lastScrollY;
          }
          break;
      }

      // Aplicar estado sticky
      if (shouldBeSticky !== this.isSticky) {
        this.isSticky = shouldBeSticky;
        this.updateStickyState();
      }

      // Atualizar último scroll
      this.lastScrollY = currentScrollY;
    }

    /**
     * Atualizar Estado Sticky
     */
    updateStickyState() {
      if (this.isSticky) {
        this.header.classList.add('is-sticky');
        document.body.classList.add('header-is-sticky');

        // Definir variável CSS com altura do header
        document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);

        // Adicionar padding ao body para compensar header fixo
        document.body.style.paddingTop = `${this.headerHeight}px`;

        // Esconder gradiente
        if (this.gradient) {
          this.gradient.style.opacity = '0';
          this.gradient.style.visibility = 'hidden';
        }

        // Disparar evento
        this.header.dispatchEvent(new CustomEvent('header:sticky', {
          detail: { active: true }
        }));
      } else {
        this.header.classList.remove('is-sticky');
        document.body.classList.remove('header-is-sticky');
        document.body.style.paddingTop = '0';

        // Mostrar gradiente (apenas na HOME)
        if (this.gradient && this.headerMode === 'home') {
          this.gradient.style.opacity = '1';
          this.gradient.style.visibility = 'visible';
        }

        // Disparar evento
        this.header.dispatchEvent(new CustomEvent('header:sticky', {
          detail: { active: false }
        }));
      }
    }

    /**
     * Configurar Dropdowns
     */
    setupDropdowns() {
      this.menuItems.forEach(item => {
        const link = item.querySelector('.header-btg__menu-link');
        const dropdown = item.querySelector('.header-btg__dropdown');

        if (!link || !dropdown) return;

        // Hover para desktop
        item.addEventListener('mouseenter', () => {
          link.setAttribute('aria-expanded', 'true');
        });

        item.addEventListener('mouseleave', () => {
          link.setAttribute('aria-expanded', 'false');
        });

        // Click para mobile/touch
        link.addEventListener('click', (e) => {
          if (window.innerWidth <= 768) {
            e.preventDefault();
            const isExpanded = link.getAttribute('aria-expanded') === 'true';
            link.setAttribute('aria-expanded', !isExpanded);
            dropdown.style.display = isExpanded ? 'none' : 'block';
          }
        });
      });
    }

    /**
     * Configurar Hamburger Menu
     */
    setupHamburger() {
      this.hamburger.addEventListener('click', () => {
        const isExpanded = this.hamburger.getAttribute('aria-expanded') === 'true';
        this.hamburger.setAttribute('aria-expanded', !isExpanded);

        // Toggle classe no body para menu mobile
        document.body.classList.toggle('header-mobile-open');

        // Disparar evento customizado
        const event = new CustomEvent('header:mobile-toggle', {
          detail: { isOpen: !isExpanded }
        });
        document.dispatchEvent(event);
      });
    }

    /**
     * Configurar Acessibilidade
     */
    setupAccessibility() {
      // Navegação por teclado nos dropdowns
      this.menuItems.forEach(item => {
        const link = item.querySelector('.header-btg__menu-link');
        const dropdown = item.querySelector('.header-btg__dropdown');
        const dropdownLinks = dropdown?.querySelectorAll('.header-btg__dropdown-link');

        if (!link || !dropdownLinks) return;

        // Enter/Space para abrir dropdown
        link.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const isExpanded = link.getAttribute('aria-expanded') === 'true';
            link.setAttribute('aria-expanded', !isExpanded);

            if (!isExpanded && dropdownLinks.length > 0) {
              dropdownLinks[0].focus();
            }
          }

          // Arrow Down para abrir e focar primeiro item
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            link.setAttribute('aria-expanded', 'true');
            if (dropdownLinks.length > 0) {
              dropdownLinks[0].focus();
            }
          }
        });

        // Navegação dentro do dropdown
        dropdownLinks.forEach((dropdownLink, index) => {
          dropdownLink.addEventListener('keydown', (e) => {
            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                if (index < dropdownLinks.length - 1) {
                  dropdownLinks[index + 1].focus();
                }
                break;

              case 'ArrowUp':
                e.preventDefault();
                if (index > 0) {
                  dropdownLinks[index - 1].focus();
                } else {
                  link.focus();
                }
                break;

              case 'Escape':
                e.preventDefault();
                link.setAttribute('aria-expanded', 'false');
                link.focus();
                break;

              case 'Tab':
                if (e.shiftKey && index === 0) {
                  link.setAttribute('aria-expanded', 'false');
                } else if (!e.shiftKey && index === dropdownLinks.length - 1) {
                  link.setAttribute('aria-expanded', 'false');
                }
                break;
            }
          });
        });
      });

      // Fechar dropdown ao clicar fora
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.header-btg__menu-item--has-dropdown')) {
          this.menuItems.forEach(item => {
            const link = item.querySelector('.header-btg__menu-link');
            if (link) {
              link.setAttribute('aria-expanded', 'false');
            }
          });
        }
      });

      // Fechar com Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.menuItems.forEach(item => {
            const link = item.querySelector('.header-btg__menu-link');
            if (link) {
              link.setAttribute('aria-expanded', 'false');
            }
          });

          // Fechar menu mobile
          if (this.hamburger) {
            this.hamburger.setAttribute('aria-expanded', 'false');
            document.body.classList.remove('header-mobile-open');
          }
        }
      });
    }

    /**
     * Utilitário: Debounce
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }

  /**
   * Inicializar quando DOM estiver pronto
   */
  function initHeader() {
    // Verificar se o header existe
    if (document.querySelector('.header-btg')) {
      new HeaderBTG();
    }
  }

  // Inicializar
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeader);
  } else {
    initHeader();
  }

  // Expor classe globalmente para debug
  window.HeaderBTG = HeaderBTG;

})();
